import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import Chart from "chart.js";
import {
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Container,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

import { chartOptions, parseOptions } from "variables/charts.js";

import { API_UPDATESETTING, API_GETSETTING } from "../../config/api";
import { api } from "../../config/middleware";
import { useSessionStore } from "../../stores/sessionStore";
import { useSettingStore } from "../../stores/settingStore";
import Swal from "sweetalert2";
import LoadingSpinner from "components/LoadingSpinner";

const DEFAULT_SETTING = {
  billMode: "pos",
  vatEnabled: "unabledVat",
  taxMode: "separate",
  shopType: "retail",
  printerType: "usb",
  printerIp: "192.168.1.100",
  pointRate: 25,
};

const Setting = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const role = (user?.role || localStorage.getItem("role") || "user")
    .toLowerCase()
    .trim();
  const isAdmin = role === "admin";

  const tnId = useSessionStore((s) => s.profile?.tnId);
  const orgId = useSessionStore((s) => s.profile?.orgId);
  const brId = useSessionStore((s) => s.profile?.brId);

  const setBillModeMenu = useSettingStore((s) => s.setBillMode);

  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  // ===== state สำหรับ UI =====
  const [vatEnabled, setVatEnabled] = useState(DEFAULT_SETTING.vatEnabled);
  const [billMode, setBillMode] = useState(DEFAULT_SETTING.billMode);
  const [taxMode, setTaxMode] = useState(DEFAULT_SETTING.taxMode);

  const [shopType, setShopType] = useState(DEFAULT_SETTING.shopType);
  const [printerType, setPrinterType] = useState(DEFAULT_SETTING.printerType);
  const [printerIp, setPrinterIp] = useState(DEFAULT_SETTING.printerIp);

  const [pointRate, setPointRate] = useState(DEFAULT_SETTING.pointRate);

  // ✅ loading states
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (brId == null) return;

    const fetchSetting = async () => {
      setIsLoading(true);
      try {
        const res = await api.get(API_GETSETTING, {
          params: { BRID: brId },
        });

        if (res.data?.statustext !== "SUCCESS") return;

        const s = res.data.data;

        setBillMode(s.billMode === 1 ? "pos" : "general");
        setVatEnabled(s.vatMode === 1 ? "unabledVat" : "enabledVat");
        setTaxMode(s.calculationTax === 1 ? "separate" : "include");
        setShopType(s.storeType === 1 ? "retail" : "cafe");
        setPrinterType(s.printerType === 1 ? "usb" : "wifi");

        // sync เข้า zustand ตาม brId
        setBillModeMenu(brId, s.billMode ?? 1);
      } catch (err) {
        console.error("GetSetting error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSetting();
  }, [brId, setBillModeMenu]);

  const buildSettingPayload = () => ({
    BRID: brId,
    billMode: billMode === "pos" ? 1 : 0,
    vatMode: vatEnabled === "unabledVat" ? 1 : 0,
    calculationTax: taxMode === "separate" ? 1 : 0,
    storeType: shopType === "retail" ? 1 : 0,
    printerType: printerType === "usb" ? 1 : 0,
  });

  const handleSaveSetting = async () => {
    if (saving) return;
    try {
      setSaving(true);

      const payload = buildSettingPayload();
      const res = await api.post(API_UPDATESETTING, payload);

      if (res.data?.statustext === "SUCCESS") {
        setBillModeMenu(brId, payload.billMode);

        Swal.fire({
          icon: "success",
          title: "อัปเดตสำเร็จ",
          timer: 1200,
          showConfirmButton: false,
        });
      } else {
        Swal.fire(
          "ผิดพลาด",
          res.data?.message || "ไม่สามารถอัปเดตได้",
          "error",
        );
      }
    } catch (err) {
      console.error(err);
      Swal.fire("ผิดพลาด", "เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetSetting = () => {
    if (saving) return;

    Swal.fire({
      title: "ยืนยันรีเซ็ตการตั้งค่า",
      text: "ระบบจะกลับไปใช้ค่าเริ่มต้นทั้งหมด",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "รีเซ็ต",
      cancelButtonText: "ยกเลิก",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        setSaving(true);

        // reset UI
        setBillMode(DEFAULT_SETTING.billMode);
        setVatEnabled(DEFAULT_SETTING.vatEnabled);
        setTaxMode(DEFAULT_SETTING.taxMode);
        setShopType(DEFAULT_SETTING.shopType);
        setPrinterType(DEFAULT_SETTING.printerType);
        setPrinterIp(DEFAULT_SETTING.printerIp);
        setPointRate(DEFAULT_SETTING.pointRate);

        await api.post(API_UPDATESETTING, {
          BRID: brId,
          billMode: 1,
          vatMode: 1,
          calculationTax: 1,
          storeType: 1,
          printerType: 1,
        });

        setBillModeMenu(brId, 1);

        Swal.fire({
          icon: "success",
          title: "รีเซ็ตเรียบร้อย",
          timer: 1200,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error(err);
        Swal.fire(
          "ผิดพลาด",
          "เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์",
          "error",
        );
      } finally {
        setSaving(false);
      }
    });
  };

  if (isLoading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <>
      {/* Header */}
      <div className="header bg-gradient-info pb-6 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body"></div>
        </Container>
      </div>

      {/* Page content */}
      {isAdmin ? (
        <Container className="mt--7" fluid>
          <>
            <Row className="align-items-stretch">
              {/* ตั้งค่าทั่วไป */}
              <Col xl="6" lg="6" className="mb-4 d-flex">
                <Card className="shadow-sm h-100 w-100">
                  <CardHeader className="border-0 pb-2">
                    <CardTitle tag="h5" className="mb-0">
                      ตั้งค่าทั่วไป
                    </CardTitle>
                  </CardHeader>

                  <CardBody>
                    <div className="mb-2" style={{ fontWeight: 500 }}>
                      โหมดเปิดบิล
                    </div>

                    <ButtonGroup className="mb-2">
                      <Button
                        size="sm"
                        color={billMode === "pos" ? "primary" : "secondary"}
                        outline={billMode !== "pos"}
                        onClick={() => setBillMode("pos")}
                        disabled={saving}
                      >
                        POS
                      </Button>

                      <Button
                        size="sm"
                        color={billMode === "general" ? "primary" : "secondary"}
                        outline={billMode !== "general"}
                        onClick={() => setBillMode("general")}
                        disabled={saving}
                      >
                        ทั่วไป
                      </Button>
                    </ButtonGroup>

                    <div className="mb-2" style={{ fontWeight: 500 }}>
                      VAT 7%
                    </div>

                    <ButtonGroup className="mb-2">
                      <Button
                        size="sm"
                        color={
                          vatEnabled === "unabledVat" ? "primary" : "secondary"
                        }
                        outline={vatEnabled !== "unabledVat"}
                        onClick={() => setVatEnabled("unabledVat")}
                        disabled={saving}
                      >
                        ไม่คำนวณ
                      </Button>

                      <Button
                        size="sm"
                        color={
                          vatEnabled === "enabledVat" ? "primary" : "secondary"
                        }
                        outline={vatEnabled !== "enabledVat"}
                        onClick={() => setVatEnabled("enabledVat")}
                        disabled={saving}
                      >
                        คำนวณ
                      </Button>
                    </ButtonGroup>

                    <div className="mb-2" style={{ fontWeight: 500 }}>
                      การคำนวณภาษี
                    </div>

                    <ButtonGroup className="mb-2">
                      <Button
                        size="sm"
                        color={taxMode === "separate" ? "primary" : "secondary"}
                        outline={taxMode !== "separate"}
                        onClick={() => setTaxMode("separate")}
                        disabled={saving}
                      >
                        แยกภาษี
                      </Button>

                      <Button
                        size="sm"
                        color={taxMode === "include" ? "primary" : "secondary"}
                        outline={taxMode !== "include"}
                        onClick={() => setTaxMode("include")}
                        disabled={saving}
                      >
                        รวมภาษี
                      </Button>
                    </ButtonGroup>

                    <div className="text-muted" style={{ fontSize: 12 }}>
                      ราคาสินค้าที่แสดงเป็นราคาก่อนภาษี ระบบจะคิด VAT
                      เพิ่มในบิลยอดสุดท้าย
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* ตั้งค่าร้านค้าและอุปกรณ์ */}
              <Col xl="6" lg="6" className="mb-4 d-flex">
                <Card className="shadow-sm h-100 w-100">
                  <CardHeader className="border-0 pb-2">
                    <CardTitle tag="h5" className="mb-0">
                      ตั้งค่าร้านค้าและอุปกรณ์
                    </CardTitle>
                  </CardHeader>

                  <CardBody>
                    <div className="mb-2" style={{ fontWeight: 500 }}>
                      ประเภทร้านค้า
                    </div>

                    <ButtonGroup className="mb-4">
                      <Button
                        size="sm"
                        color={shopType === "retail" ? "primary" : "secondary"}
                        outline={shopType !== "retail"}
                        onClick={() => setShopType("retail")}
                        disabled={saving}
                      >
                        ธุรกิจขนาดเล็ก
                      </Button>

                      <Button
                        size="sm"
                        color={shopType === "cafe" ? "primary" : "secondary"}
                        outline={shopType !== "cafe"}
                        onClick={() => setShopType("cafe")}
                        disabled={saving}
                      >
                        ธุรกิจขนาดกลาง
                      </Button>
                    </ButtonGroup>

                    <div className="mb-2" style={{ fontWeight: 500 }}>
                      เครื่องพิมพ์
                    </div>

                    <ButtonGroup className="mb-3">
                      <Button
                        size="sm"
                        color={printerType === "usb" ? "primary" : "secondary"}
                        outline={printerType !== "usb"}
                        onClick={() => setPrinterType("usb")}
                        disabled={saving}
                      >
                        USB
                      </Button>

                      <Button
                        size="sm"
                        color={printerType === "wifi" ? "primary" : "secondary"}
                        outline={printerType !== "wifi"}
                        onClick={() => setPrinterType("wifi")}
                        disabled={saving}
                      >
                        Wi-Fi/Network
                      </Button>
                    </ButtonGroup>

                    {printerType === "wifi" && (
                      <FormGroup>
                        <Label className="form-control-label">
                          IP Address เครื่องพิมพ์
                        </Label>
                        <Input
                          type="text"
                          value={printerIp}
                          onChange={(e) => setPrinterIp(e.target.value)}
                          placeholder="เช่น 192.168.1.100"
                          disabled={saving}
                        />
                      </FormGroup>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col className="text-right">
                <Button
                  color="secondary"
                  onClick={handleResetSetting}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner-border spinner-border-sm mr-2" />
                  ) : null}
                  รีเซ็ตการตั้งค่า
                </Button>

                <Button
                  color="warning"
                  onClick={handleSaveSetting}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner-border spinner-border-sm mr-2" />
                  ) : null}
                  อัปเดต
                </Button>
              </Col>
            </Row>
          </>
        </Container>
      ) : (
        <Container className="mt--7" fluid>
          <>
            <Row className="align-items-stretch">
              {/* ตั้งค่าทั่วไป */}
              <Col xl="6" lg="6" className="mb-4 d-flex">
                <Card className="shadow-sm h-100 w-100">
                  <CardHeader className="border-0 pb-2">
                    <CardTitle tag="h5" className="mb-0">
                      ตั้งค่าทั่วไป
                    </CardTitle>
                  </CardHeader>

                  <CardBody>
                    <div className="mb-2" style={{ fontWeight: 500 }}>
                      โหมดเปิดบิล
                    </div>

                    <ButtonGroup className="mb-2">
                      <Button
                        size="sm"
                        color={billMode === "pos" ? "primary" : "secondary"}
                        outline={billMode !== "pos"}
                        onClick={() => setBillMode("pos")}
                        disabled={saving}
                      >
                        POS
                      </Button>

                      <Button
                        size="sm"
                        color={billMode === "general" ? "primary" : "secondary"}
                        outline={billMode !== "general"}
                        onClick={() => setBillMode("general")}
                        disabled={saving}
                      >
                        ทั่วไป
                      </Button>
                    </ButtonGroup>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col className="text-right">
                <Button
                  color="secondary"
                  onClick={handleResetSetting}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner-border spinner-border-sm mr-2" />
                  ) : null}
                  รีเซ็ตการตั้งค่า
                </Button>

                <Button
                  color="warning"
                  onClick={handleSaveSetting}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner-border spinner-border-sm mr-2" />
                  ) : null}
                  อัปเดต
                </Button>
              </Col>
            </Row>
          </>
        </Container>
      )}
    </>
  );
};

export default Setting;
